/**
 * Production Console Suppression Utility
 * Suppresses all console warnings, errors, and logs in production mode
 * to prevent users from seeing technical details in browser console
 */

// Check if we're in production mode
const isProduction = process.env.NODE_ENV === 'production';

if (isProduction) {
  // Create no-op function for all console methods
  const noop = function() {};
  
  // Override all console methods to be no-ops in production
  const consoleMethods = [
    'warn', 'error', 'log', 'info', 'debug', 'trace',
    'group', 'groupEnd', 'groupCollapsed', 'table',
    'dir', 'dirxml', 'assert', 'count', 'countReset',
    'time', 'timeEnd', 'timeLog', 'profile', 'profileEnd'
  ];

  consoleMethods.forEach(method => {
    if (typeof console[method] === 'function') {
      try {
        console[method] = noop;
      } catch (e) {
        // Some console methods might be read-only, ignore errors
      }
    }
  });

  // Suppress React DevTools warnings and hooks
  if (typeof window !== 'undefined') {
    // Suppress React DevTools
    if (window.__REACT_DEVTOOLS_GLOBAL_HOOK__) {
      try {
        window.__REACT_DEVTOOLS_GLOBAL_HOOK__.onCommitFiberRoot = noop;
        window.__REACT_DEVTOOLS_GLOBAL_HOOK__.onCommitFiberUnmount = noop;
        window.__REACT_DEVTOOLS_GLOBAL_HOOK__.onCommitFiberRoot = noop;
      } catch (e) {
        // Ignore errors
      }
    }

    // Suppress common third-party library warnings
    // Override window.console to prevent direct access
    try {
      const originalConsole = window.console;
      const suppressedConsole = {};
      
      // Copy all console properties but override methods
      Object.keys(originalConsole).forEach(key => {
        if (typeof originalConsole[key] === 'function') {
          suppressedConsole[key] = noop;
        } else {
          suppressedConsole[key] = originalConsole[key];
        }
      });
      
      // Make console methods non-writable in production
      Object.keys(suppressedConsole).forEach(key => {
        try {
          Object.defineProperty(console, key, {
            value: suppressedConsole[key],
            writable: false,
            configurable: false
          });
        } catch (e) {
          // Some properties might be read-only, ignore
        }
      });
    } catch (e) {
      // Fallback: just override methods directly
      console.warn = noop;
      console.error = noop;
      console.log = noop;
      console.info = noop;
      console.debug = noop;
    }

    // Suppress unhandled errors and promise rejections from showing in console
    // Note: Errors are still thrown and can be caught by error boundaries,
    // but they won't be logged to console
    const originalErrorHandler = window.onerror;
    window.onerror = function(message, source, lineno, colno, error) {
      // Suppress console output but don't prevent error propagation
      // Error boundaries will still catch React errors
      return true; // Return true to prevent default console error logging
    };

    const originalUnhandledRejection = window.onunhandledrejection;
    window.addEventListener('unhandledrejection', function(event) {
      // Suppress console output for unhandled promise rejections
      event.stopImmediatePropagation();
    }, true);
  }
}

// Export for potential use in other files
export default {
  isProduction,
  suppressConsole: isProduction
};

